import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { isSessionTransferInFlight } from '../SessionTransfer/SessionTransfer.clientState';
import { useAuth0Token } from '../useAuth0Token';
import { ConsentUpdateRequest, MemberCustomerConsents } from '../../../../types/Consents';

export const useConsentsApi = (isNewCustomer = false) => {
  const [consents, setConsents] = useState<MemberCustomerConsents>();
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isError, setIsError] = useState(false);
  const { simpleToken, loading: isTokenLoading } = useAuth0Token();

  const getConsents = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const url = isNewCustomer
        ? ApiPaths[ApiType.apimMembershipApi].consents.newMember
        : ApiPaths[ApiType.apimMembershipApi].consents.customer;

      const res = await apiClient.get<MemberCustomerConsents>(ApiType.apimMembershipApi, { url });
      setConsents(res.data);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [isNewCustomer]);

  const updateConsents = async (updatedConsents: ConsentUpdateRequest): Promise<boolean> => {
    setIsUpdating(true);
    try {
      const res = await apiClient.put<MemberCustomerConsents>(ApiType.apimMembershipApi, {
        url: ApiPaths[ApiType.apimMembershipApi].consents.customer,
        data: { ...updatedConsents, updateSourceName: 'naf.no' },
      });
      setConsents(res.data);
      return true;
    } catch {
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  // The app opens this page mid-SSO handoff, where Auth0 reports the user as authenticated a
  // commit before the access token exists. Fetching on mount would go out without an
  // Authorization header and render the error state until something reloaded the page.
  // Both flags are booleans so the effect runs on a real transition rather than on every
  // token-state tick, which keeps the token-less signup flow to a single request.
  const isReadyToFetch = isNewCustomer || (!isTokenLoading && !!simpleToken);
  const isTokenUnavailable = !isNewCustomer && !isTokenLoading && !simpleToken;

  useEffect(() => {
    if (isReadyToFetch) {
      getConsents();
      return;
    }

    // A session transfer redirect is a full navigation: the page is about to be replaced, so
    // showing an error here would flash a red box the reload immediately discards.
    if (isTokenUnavailable && !isSessionTransferInFlight()) {
      setIsError(true);
      setIsLoading(false);
    }
  }, [getConsents, isReadyToFetch, isTokenUnavailable]);

  return { consents, isLoading, isUpdating, isError, updateConsents, setConsents, refetch: getConsents };
};
